import {
  BankOfferDataModel,
  filmCinemasResponse,
  filmExperiencesResponse,
  filmsUrl,
  movieDetailsResponse,
  UnipalInitOrderModel,
  useFilmRemindMe,
  useGetMovieCinemas,
  useGetMovieCinemasFilter,
  useGetMovieDetailsBySlug,
  useGetMovieExperiencesFilter,
  useRemoveReminder,
} from '@/logic/services';
import {
  Box,
  Button,
  Drawer,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  styled,
  useTheme,
} from '@mui/material';

import { FormattedMessage, useIntl } from 'react-intl';
import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { QueryClient, dehydrate, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect } from 'react';

import { useAuth, useMovieDetailsState, useSnack } from '@/logic/state';
import { useCity, useMovieDetails, useRouteChangeHandler } from '@/logic/hooks';

import { CinemaModel } from '@/logic/services/cinemas';

import Icon24 from '@/components/shared/icon-24';
import Link from 'next/link';
import { LoadingButton } from '@mui/lab';
import { MovieTypesEnum } from '@/constants/movie-types-enum';
import OfferApprovedLottie from '../../../lotties/muvi_offer_approved.json';
import OfferBadge from '@/components/offers/offer-badge';
import OfferLottie from '../../../lotties/muvi_offer.json';
import SelectedFiltersTags from '@/components/movie-details/filters/selected-filters-tags';
import Seo from '@/components/seo/seo';
import { ShowtimesProvider } from '@/hooks/use-showtimes-provider';
import axios from 'axios';
import { buildEventSchemaComponent } from '@/logic/helpers';

import getLangHeaders from '@/logic/helpers/get-lang-headers';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useOfferHandler } from '@/logic/hooks';
import { useRouter } from 'next/router';
import OfferErrorBadge from '@/components/offers/offer-error-badge';
import OfferErrorIcon from '/public/icons/offers/offer-error-badge.svg';
import { CheckGlobalSsrErrors } from '@/helpers/check-global-ssr-errors';
import { Currency, EventTypes, ViewItemModel } from '@/logic/types/data-layer-models';
import { triggerGAEvent } from '@/logic/shared/data-layer-events';
import {
  bookingPageRegExp,
  checkoutPageRegExp,
  moviePageRegExp,
} from '@/constants/regular-expressions';
import MovieBanner from '@/components/movie-details/movie-details-banner';
import ImageBoxFuture from '@/components/shared/image-box-future';
import DaysFilter from '@/components/movie-details/showtimes/days-filter';
import MovieFilters from '@/components/movie-details/filters/movie-filters';
import ExperienceCollapseList from '@/components/movie-details/showtimes/experience-collapse-list';
import { CloseButton } from '@/components/shared/close-button';
import MovieInfo from '@/components/shared/movie-details-info';
import loadIntlMessages from '@/logic/helpers/loadIntlMessages';

const filterIcon = '/icons/shared/filter.svg';
const BillIcon = '/icons/movie-details/bill-white-icon.svg';
const ComingSoonIcon = '/icons/movie-details/coming-soon.svg';

const MovieDetails: NextPage<{
  serverCityId: number;
  path: string;
  movieLocations?: CinemaModel[];
  serverMovie: movieDetailsResponse;
  serverMovieCinemasFilter: filmCinemasResponse;
  serverMovieExperiencesFilter: filmExperiencesResponse;
}> = ({
  path,
  movieLocations,
  serverMovie,
  serverMovieCinemasFilter,
  serverMovieExperiencesFilter,
}) => {
  const theme = useTheme();
  const router = useRouter();
  const intl = useIntl();
  const user = useAuth(s => s.user, shallow);
  const setSnack = useSnack(s => s.setSnack, shallow);
  const queryClient = useQueryClient();

  const { cityId: browserCityId, getCity } = useCity();
  const cityId = +router.query.cityId || browserCityId;

  const {
    clientMovie,
    refetch,
    handleSubmitFilters,
    handleMoviePageDeleteOffer,
    handleClickShowtime,
    handleClearFilters,
    handleLoadTheLeftCinemas,
    callOnFilmHasBeenStarted,
    removeSelectedFilter,
  } = useMovieDetails({
    serverMovie,
    serverMovieCinemasFilter,
    serverMovieExperiencesFilter,
  });

  const [
    offer,
    filmCinemas,
    showTimeDate,
    setShowTimeDate,
    setFirstEnabledDay,
    selectedFilters,
    isFiltersDrawerOpen,
    setIsFiltersDrawerOpen,
    setIsSeeResultsDisabled,
    isOfferApplied,
    isThereAtLeastOneAvailableShowTime,
    isFetching,
    movieHasOffers,
    hasOfferError,
    hasOfferLoading,
  ] = useMovieDetailsState(
    s => [
      s.offer,
      s.filmCinemas,
      s.showTimeDate,
      s.setShowTimeDate,
      s.setFirstEnabledDay,
      s.selectedFilters,
      s.isFiltersDrawerOpen,
      s.setIsFiltersDrawerOpen,
      s.setIsSeeResultsDisabled,
      s.isOfferApplied,
      s.isThereAtLeastOneAvailableShowTime,
      s.isFetching,
      s.movieHasOffers,
      s.hasOfferError,
      s.hasOfferLoading,
    ],
    shallow
  );

  const movie = clientMovie || serverMovie;
  const isSelectedDateHasOffers = movieHasOffers;
  // const isSelectedDateHasOffers = filmCinemas?.hasOffers;

  const handleOpenFiltersDrawer = () => setIsFiltersDrawerOpen(true);
  const handleCloseFiltersDrawer = () => setIsFiltersDrawerOpen(false);

  const { isBankOffer, offerTitle, handleDeleteOffer } = useOfferHandler(offer?.offer);

  const Item = styled(Paper)(({ theme }) => ({
    backgroundColor:
      theme.palette.mode === 'dark' ? '#1A2027' : theme.palette.common.white,
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  }));

  const _useFilmRemindMe = useFilmRemindMe(
    {},
    {
      onSuccess: () =>
        queryClient.invalidateQueries([
          {
            url: filmsUrl,
          },
        ]),
    }
  );
  const remindMe = () => {
    _useFilmRemindMe.safeMutateAsync(
      {
        query: {
          id: movie.film.id,
          customUrl: 'add-reminder',
        },
      },
      {
        onSuccess: () => {
          setSnack({
            isOpen: true,
            message: 'my_booking.booking_details.add_reminder.success',
            icon: <Icon24 icon={BillIcon} title='alt.bill_icon' />,
            type: 'info',
          });
          refetch();
        },
        onError: error => {
          if (axios.isAxiosError(error)) {
            setSnack({
              isOpen: true,
              message: 'global.someThingWentWrong',
              type: 'error',
            });
          }
        },
      }
    );
  };

  const _useRemoveReminder = useRemoveReminder(
    {},
    {
      onSuccess: () =>
        queryClient.invalidateQueries([
          {
            url: filmsUrl,
          },
        ]),
    }
  );

  const removeReminder = (): void => {
    _useRemoveReminder.safeMutateAsync(
      {
        query: {
          id: movie.film.id,
          customUrl: 'remove-reminder',
        },
      },
      {
        onSuccess: () => {
          setSnack({
            isOpen: true,
            message: 'movie_details.remove.notification.success',
            type: 'success',
          });
          refetch();
        },
        onError: error => {
          if (axios.isAxiosError(error)) {
            setSnack({
              isOpen: true,
              message: 'global.someThingWentWrong',
              type: 'info',
            });
          }
        },
      }
    );
  };

  const onRouteChangeStart = useCallback(() => null, []);

  const onRouteChangeComplete = useCallback(
    (nextPath: string) => {
      if (
        !moviePageRegExp.test(nextPath) &&
        !bookingPageRegExp.test(nextPath) &&
        !checkoutPageRegExp.test(nextPath)
      ) {
        handleDeleteOffer();
        router.events.off('routeChangeComplete', onRouteChangeComplete);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleDeleteOffer]
  );

  const onBeforeUnload = useCallback(() => null, []);

  useRouteChangeHandler({
    onRouteChangeStart,
    onRouteChangeComplete,
    onBeforeUnload,
  });

  const triggerDataLayerViewItemEvent = (): void => {
    const { id, title, genres, experiances, cinemas, movieLang } = movie.film;
    const data: ViewItemModel = {
      event: EventTypes.VIEW_ITEM,
      ecommerce: {
        currency: Currency.SAR,
        value: 0,
        items: [
          {
            item_id: id.toString(),
            item_name: title,
            item_brand: genres.map(genre => genre.name).join(','),
            item_category: experiances.map(experiance => experiance.name).join(','),
            item_category2: '',
            item_list_id: 'Movie Details',
            item_list_name: 'Movie Details',
            location: getCity(cityId)?.name,
            mall: cinemas.map(cinema => cinema.name).join(','),
            price: 0,
            quantity: 1,
            movie_language: movieLang,
          },
        ],
      },
    };
    triggerGAEvent(data, true);
  };

  useEffect(() => {
    triggerDataLayerViewItemEvent();
  }, []);

  return (
    <>
      {!!movie && (
        <Seo
          title={movie?.film?.titleTag || movie?.film?.title}
          canonical={movie?.film?.canonicalURL}
          pagePath={
            path.slice(0, 3) === '/en' || path.slice(0, 3) === '/ar'
              ? path
              : `${router.locale}${path}`
          }
          description={movie?.film?.descriptionTag || movie?.film?.synopsis}
          image={movie?.film?.image}
          components={() => buildEventSchemaComponent(movie?.film, movieLocations)}
          noFollow={movie?.film?.hasFollow}
          noIndex={movie?.film?.hasIndex}
        />
      )}
      {movie?.film && <MovieBanner data={movie} />}
      <Box
        sx={{
          flexGrow: 1,
          px: pxToRem(128),
          pt: pxToRem(52),
          mb: pxToRem(77),
          [theme.breakpoints.down('laptop')]: {
            px: '50px',
          },
          [theme.breakpoints.down('tablet')]: {
            px: '16px',
          },
        }}
      >
        <Grid container spacing={2}>
          {movie?.film?.type !== MovieTypesEnum.SHOW_NOW &&
          !movie?.film.isAdvancedBooking ? (
            <Grid item mobile={12} tablet={12} laptop={8}>
              <Box
                component='div'
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',

                  [theme.breakpoints.up('tablet')]: { mt: pxToRem(266) },
                  [theme.breakpoints.down('tablet')]: { mt: pxToRem(100) },
                }}
              >
                <ImageBoxFuture
                  img={ComingSoonIcon}
                  title={intl.formatMessage({
                    id: 'images.title.coming_soon',
                  })}
                  objectFit='contain'
                  sx={{
                    [theme.breakpoints.down('laptop')]: {
                      width: '220px',
                      height: '156px',
                    },
                    width: pxToRem(637),
                    height: pxToRem(451),
                  }}
                />

                <Typography
                  variant='subtitle1'
                  component='div'
                  sx={{
                    fontSize: pxToRem(24),
                    textAlign: 'center',
                    color: theme.palette.grey[50],
                    mt: pxToRem(52),
                    mb: pxToRem(40),
                    [theme.breakpoints.down('laptop')]: {
                      mt: '16px',
                      mb: '22px',
                    },
                  }}
                >
                  <FormattedMessage id='movie_details.movie_coming_soon' />
                  <br />
                  <FormattedMessage id='movie_details.miss_to_watch' />
                </Typography>

                {user && (
                  <LoadingButton
                    variant='contained'
                    sx={{
                      fontSize: '16px',
                      width: '187px',
                    }}
                    onClick={() =>
                      movie?.film.hasReminder ? removeReminder() : remindMe()
                    }
                    loading={_useFilmRemindMe.isLoading}
                  >
                    {movie?.film.hasReminder ? (
                      <FormattedMessage id='movie_details.remove.notification' />
                    ) : (
                      <FormattedMessage id='movie_details.remind_me' />
                    )}
                  </LoadingButton>
                )}
              </Box>
            </Grid>
          ) : (
            <>
              <Grid item mobile={12} mediumTablet={6} laptop={3}>
                <Item
                  sx={{
                    p: 0,
                    display: {
                      mobile: 'none',
                      mediumTablet: 'block',
                    },
                  }}
                >
                  <MovieFilters
                    removeSelectedFilter={removeSelectedFilter}
                    handleClearFilters={handleClearFilters}
                    submitFilters={() => handleSubmitFilters()}
                  />
                </Item>
              </Grid>

              <Grid item mobile={12} mediumTablet={6} laptop={5}>
                <Box>
                  <DaysFilter
                    sessionsDate={movie?.sessionsDates}
                    showTimeDate={showTimeDate}
                    returnFirstEnabledDate={setFirstEnabledDay}
                    selectDate={(date: string) => {
                      setShowTimeDate(date);
                      setIsSeeResultsDisabled(true);
                    }}
                  />

                  {isSelectedDateHasOffers && !isOfferApplied && (
                    <Box sx={{ marginY: 2, marginX: 2.75 }}>
                      <Link
                        href={{
                          pathname: `/movies/${movie.film.uniqueName}/offers`,
                          query: { date: showTimeDate, cityId: cityId },
                        }}
                        passHref
                        prefetch={false}
                      >
                        <Button component={'a'} sx={{ width: '100%' }} id='apply-offer'>
                          <OfferBadge
                            lottie={OfferLottie}
                            title={intl.formatMessage({ id: 'offer.banner.title' })}
                            subTitle={intl.formatMessage({ id: 'offer.banner.subtitle' })}
                            styles={{
                              subTitle: {
                                color: theme.palette.grey[60],
                              },
                            }}
                          />
                        </Button>
                      </Link>
                    </Box>
                  )}
                  {hasOfferError && (
                    <Box sx={{ margin: 2.75 }}>
                      <OfferErrorBadge
                        icon={OfferErrorIcon}
                        title={intl.formatMessage({ id: 'offer.error.title' })}
                        actionTitle={intl.formatMessage({ id: 'offer.error.reload' })}
                        handleAction={() =>
                          queryClient.invalidateQueries([
                            {
                              url: 'offers/check-film',
                            },
                          ])
                        }
                        isLoading={hasOfferLoading}
                      />
                    </Box>
                  )}

                  {isOfferApplied && (
                    <Box sx={{ marginY: 2 }}>
                      <OfferBadge
                        lottie={OfferApprovedLottie}
                        title={intl.formatMessage({ id: 'offer.banner.applied.title' })}
                        onDelete={handleMoviePageDeleteOffer}
                        subTitle={
                          <Stack component={'span'}>
                            <Box component={'span'}>{offerTitle}</Box>
                            {isBankOffer && (
                              <Box component={'span'}>
                                {intl.formatMessage(
                                  {
                                    id: 'offer.banner.applied.subtitle',
                                  },
                                  { bankName: offer?.offer?.bankName }
                                )}
                              </Box>
                            )}
                          </Stack>
                        }
                        styles={{
                          subTitle: {
                            color: theme.palette.grey[60],
                          },
                        }}
                      />
                    </Box>
                  )}

                  <Box
                    sx={{
                      px: pxToRem(30),
                      marginBottom: pxToRem(45),
                    }}
                  >
                    <Stack direction='row' justifyContent='space-between'>
                      {filmCinemas?.cinemas.length !== 0 && (
                        <Typography
                          id='movie-shows'
                          sx={{
                            color: theme.palette.common.black,
                            fontSize: pxToRem(20),
                            fontWeight: 700,
                            lineHeight: pxToRem(30),
                            marginTop: pxToRem(45),
                          }}
                        >
                          <FormattedMessage id='movie.shows' />
                        </Typography>
                      )}

                      {!isOfferApplied && (
                        <IconButton
                          sx={{
                            display: {
                              mobile: 'block',
                              mediumTablet: 'none',
                            },
                          }}
                          onClick={() => handleOpenFiltersDrawer()}
                        >
                          <ImageBoxFuture
                            img={filterIcon}
                            title={intl.formatMessage({
                              id: 'alt.filter_icon',
                            })}
                            objectFit='contain'
                            width='40px'
                            height='40px'
                            sx={{
                              width: '40px',
                              height: '40px',
                            }}
                          />
                        </IconButton>
                      )}
                    </Stack>

                    <Stack
                      direction='row'
                      spacing={2}
                      sx={{
                        marginBottom: 2,
                        display: {
                          mobile: 'flex',
                          tablet: 'none',
                        },
                      }}
                    >
                      {selectedFilters?.length > 0 && !isOfferApplied && (
                        <Button
                          size='small'
                          variant='lightGrey'
                          sx={{
                            mt: '1rem',
                            height: '20px',
                            borderRadius: '30px',
                            px: '12px',
                            py: '4px',
                            fontSize: pxToRem(16),
                          }}
                          onClick={handleClearFilters}
                        >
                          <FormattedMessage id='movie_details.clear.filters' />
                        </Button>
                      )}
                      <SelectedFiltersTags
                        sx={{
                          display: {
                            mobile: 'block',
                            tablet: 'none',
                          },
                        }}
                        selectedFilters={selectedFilters}
                        handleDeleteSelectedFilter={removeSelectedFilter}
                      />
                    </Stack>
                  </Box>

                  {!isThereAtLeastOneAvailableShowTime &&
                  !isFetching &&
                  !filmCinemas?.cinemas ? (
                    <>
                      <Box
                        sx={{
                          marginTop: pxToRem(80),
                          marginBottom: pxToRem(24),
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <ImageBoxFuture
                          img='/icons/movie-details/no-showtimes-available.svg'
                          title={intl.formatMessage({
                            id: 'alt.no_show_time_available',
                          })}
                          objectFit='contain'
                          sx={{
                            width: '382px',
                            height: '282px',
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          color: theme.palette.grey[50],
                          fontWeight: 400,
                          fontSize: pxToRem(20),
                          lineHeight: '30px',
                          textAlign: 'center',
                        }}
                      >
                        <FormattedMessage id='movies.there.is.no.showtimes.remove.filters' />
                      </Typography>
                    </>
                  ) : (
                    <ShowtimesProvider handleClickShowtime={handleClickShowtime}>
                      <ExperienceCollapseList
                        movieId={movie.film.id}
                        cinemas={filmCinemas?.cinemas}
                        loadTheLeftCinemas={handleLoadTheLeftCinemas}
                        callOnFilmHasBeenStarted={callOnFilmHasBeenStarted}
                        showTimeDate={showTimeDate}
                      />
                    </ShowtimesProvider>
                  )}
                </Box>

                <Drawer
                  anchor='bottom'
                  open={isFiltersDrawerOpen}
                  onClose={handleCloseFiltersDrawer}
                  sx={{
                    '.MuiDrawer-paper': {
                      height: '100%',
                    },
                  }}
                >
                  <Box
                    component='div'
                    sx={{
                      py: '10px',
                    }}
                    onClick={() => handleCloseFiltersDrawer()}
                  >
                    <CloseButton
                      sx={{
                        position: 'static',
                      }}
                    />
                  </Box>

                  <Box>
                    <MovieFilters
                      removeSelectedFilter={removeSelectedFilter}
                      handleClearFilters={() => handleClearFilters()}
                      submitFilters={() => {
                        handleSubmitFilters();
                        handleCloseFiltersDrawer();
                      }}
                    />
                  </Box>
                </Drawer>
              </Grid>
            </>
          )}
          <Grid
            item
            mobile={12}
            mediumTablet={6}
            laptop={4}
            sx={{
              [theme.breakpoints.down('laptop')]: {
                display: 'none',
              },
            }}
          >
            <MovieInfo data={movie} />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const headers = {
    ...getLangHeaders(context.locale),
  };
  const { locale, defaultLocale } = context;
  const cityId = Number(context.query.cityId || context.req.cookies.city);
  const offerId = context.query.offerId;
  // const offerId = Number(context.query.offerId);
  const bankOfferDataBin = context.req.cookies.bankOfferDataBin;
  const bankOfferDataLast4 = context.req.cookies.bankOfferDataLast4;
  const bankOfferDataExpiryMonth = context.req.cookies.bankOfferDataExpiryMonth;
  const bankOfferDataExpiryYear = context.req.cookies.bankOfferDataExpiryYear;
  const unipalOfferStudentId = context.req.cookies.unipalStudentId;

  const bankOfferData: BankOfferDataModel =
    bankOfferDataBin &&
    bankOfferDataLast4 &&
    bankOfferDataExpiryMonth &&
    bankOfferDataExpiryYear
      ? {
          bin: bankOfferDataBin,
          last4: bankOfferDataLast4,
          expiryMonth: bankOfferDataExpiryMonth,
          expiryYear: bankOfferDataExpiryYear,
        }
      : {};

  const unipalOfferData: Pick<UnipalInitOrderModel, 'unipalStudentId'> =
    unipalOfferStudentId
      ? {
          unipalStudentId: unipalOfferStudentId,
        }
      : {};

  const movieSlug = (context.query.slug as string) ?? null;
  const queryClient = new QueryClient();
  let movieLocations: CinemaModel[] = [];
  let movie: movieDetailsResponse = null;
  let movieCinemasFilter: filmCinemasResponse = null;
  let movieExperiencesFilter: filmExperiencesResponse = null;

  try {
    if (movieSlug) {
      movie = await useGetMovieDetailsBySlug.fetchQuery(queryClient, {
        query: { slug: movieSlug as string },
        params: {
          cityId: !isNaN(cityId) ? cityId.toString() : undefined,
          ...(!!offerId && {
            offerId: offerId,
            // offerId: offerId?.toString(),
          }),
          ...bankOfferData,
          ...unipalOfferData,
        },
        headers,
      });
    }

    if (movie && movie.sessionsDates?.length > 0 && !!cityId) {
      movieCinemasFilter = await useGetMovieCinemasFilter.fetchQuery(queryClient, {
        params: {
          filmId: movie?.film.id,
          date: movie?.sessionsDates[0].date,
          cityId,
          experienceIds: [],
        },
        headers,
      });

      movieExperiencesFilter = await useGetMovieExperiencesFilter.fetchQuery(
        queryClient,
        {
          params: {
            filmId: movie?.film.id,
            date: movie?.sessionsDates[0].date,
            cityId,
            cinemaIds: [],
          },
          headers,
        }
      );

      const movieLocationsList: filmCinemasResponse = await useGetMovieCinemas.fetchQuery(
        queryClient,
        {
          query: { id: movie?.film.id },
          params: {
            cityId,
            cinemaIds: [],
            filterCinemaIds: [],
            filterExperienceIds: [],
            showMore: false,
            date: movie?.sessionsDates[0]?.date,
            ...bankOfferData,
            ...unipalOfferData,
          },
          headers,
        }
      );

      const movieLocationsListInverse: filmCinemasResponse =
        await useGetMovieCinemas.fetchQuery(queryClient, {
          query: { id: movie?.film.id },
          params: {
            cityId,
            cinemaIds: [],
            filterCinemaIds: [],
            filterExperienceIds: [],
            showMore: true,
            date: movie?.sessionsDates[0]?.date,
            ...bankOfferData,
          },
          headers,
        });
      movieLocations = [
        ...movieLocationsList.cinemas,
        ...movieLocationsListInverse.cinemas,
      ];
    }
  } catch (error) {
    return CheckGlobalSsrErrors(error);
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      path: context.resolvedUrl,
      movieLocations,
      serverMovie: movie,
      serverMovieCinemasFilter: movieCinemasFilter,
      serverMovieExperiencesFilter: movieExperiencesFilter,
      intlMessages: await loadIntlMessages(locale as string, defaultLocale),
    },
  };
};

export default MovieDetails;
